import { Box } from '@mui/material';
import { ReactElement } from 'react';
import {
  BooleanField,
  DataTable,
  FunctionField,
  ListContextProvider,
  NumberField,
  ReferenceField,
  ResourceContextProvider,
  SelectField,
  TextField,
  useList,
  type SortPayload,
} from 'react-admin';
import { reportDataTableSx } from '../../../../Components/ReportsWidget/utils';
import type { ClicksByMerchantsResource } from '../../../../Entities/ClicksByMerchant';
import { BILLING_TYPE_CHOICES, PAYMENT_TYPE_CHOICES } from '../../../Merchant/utils';

export interface ClicksByMerchantDataTableProps {
  data: ClicksByMerchantsResource[];
  isPending?: boolean;
  sort?: SortPayload;
  startDateLabel: string;
  endDateLabel: string;
  canListMerchantStatuses: boolean;
}

export default function ClicksByMerchantDataTable({
  data,
  isPending = false,
  sort,
  startDateLabel,
  endDateLabel,
  canListMerchantStatuses,
}: ClicksByMerchantDataTableProps): ReactElement {
  const listContext = useList<ClicksByMerchantsResource>({
    data,
    isPending,
    sort,
    perPage: Math.max(data.length, 1),
  });

  const merchantReferenceQueryOptions = { meta: { prefetch: ['merchant_status'] } };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <ListContextProvider value={listContext}>
        <ResourceContextProvider value="clicks_by_merchants">
          <DataTable rowClick={false} bulkActionButtons={false} size="small" sx={reportDataTableSx}>
            <DataTable.Col label="Region">
              <ReferenceField
                source="relationships.region.data.id"
                reference="regions"
                link={false}
              >
                <TextField source="attributes.name" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="relationships.merchant.data.id" label="Merchant ID">
              <TextField source="relationships.merchant.data.id" />
            </DataTable.Col>
            <DataTable.Col label="Merchant Name" disableSort>
              <ReferenceField
                source="relationships.merchant.data.id"
                reference="merchants"
                queryOptions={merchantReferenceQueryOptions}
              >
                <TextField source="attributes.merchant_name" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col label="Start Date" disableSort>
              <FunctionField render={() => startDateLabel} />
            </DataTable.Col>
            <DataTable.Col label="End Date" disableSort>
              <FunctionField render={() => endDateLabel} />
            </DataTable.Col>
            {canListMerchantStatuses ? (
              <DataTable.Col label="Status" disableSort>
                <ReferenceField
                  source="relationships.merchant.data.id"
                  reference="merchants"
                  link={false}
                  queryOptions={merchantReferenceQueryOptions}
                >
                  <ReferenceField
                    source="relationships.merchant_status.data.id"
                    reference="merchant_statuses"
                    link={false}
                  >
                    <TextField source="attributes.name" />
                  </ReferenceField>
                </ReferenceField>
              </DataTable.Col>
            ) : null}
            <DataTable.Col label="Payment Type" disableSort>
              <ReferenceField
                source="relationships.merchant.data.id"
                reference="merchants"
                link={false}
                queryOptions={merchantReferenceQueryOptions}
              >
                <SelectField
                  source="attributes.payment_type_code"
                  choices={PAYMENT_TYPE_CHOICES}
                  optionValue="id"
                  optionText="name"
                />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col label="Billing Type" disableSort>
              <ReferenceField
                source="relationships.merchant.data.id"
                reference="merchants"
                link={false}
                queryOptions={merchantReferenceQueryOptions}
              >
                <SelectField
                  source="attributes.billing_type_code"
                  choices={BILLING_TYPE_CHOICES}
                  optionValue="id"
                  optionText="name"
                />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col label="Is Foreign Company?" disableSort>
              <ReferenceField
                source="relationships.merchant.data.id"
                reference="merchants"
                link={false}
                queryOptions={merchantReferenceQueryOptions}
              >
                <BooleanField source="attributes.is_foreign_company" looseValue />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col label="Monthly Budget" disableSort>
              <ReferenceField
                source="relationships.merchant.data.id"
                reference="merchants"
                link={false}
                queryOptions={merchantReferenceQueryOptions}
              >
                <NumberField
                  source="attributes.monthly_budget"
                  options={{ style: 'currency', currency: 'AUD' }}
                />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.NumberCol source="attributes.total_clicks" label="Total Clicks" />
            <DataTable.NumberCol source="attributes.chargeable_clicks" label="Chargeable" />
            <DataTable.NumberCol source="attributes.non_chargeable_clicks" label="Non-Chargeable" />
            <DataTable.NumberCol source="attributes.bidding_clicks" label="Bidding" />
            <DataTable.NumberCol source="attributes.getprice_clicks" label="Getprice" />
            <DataTable.NumberCol source="attributes.future_clicks" label="Future" />
            <DataTable.NumberCol
              source="attributes.chargeable_income"
              label="Chargeable Income "
              options={{ style: 'currency', currency: 'AUD' }}
            />
            <DataTable.NumberCol
              source="attributes.non_chargeable_income"
              label="Non-Chargeable Income "
              options={{ style: 'currency', currency: 'AUD' }}
            />
            <DataTable.NumberCol
              source="attributes.total_cpc_income"
              label="Total CPC Income "
              options={{ style: 'currency', currency: 'AUD' }}
            />
          </DataTable>
        </ResourceContextProvider>
      </ListContextProvider>
    </Box>
  );
}
